import type { QuestionModule } from "../contracts";
import { rowSpecifyFields } from "../specify/rowFields";
import {
  gridSelectCore,
  type GridSelectDefinition,
  type GridSelectDraft,
  type GridSelectEvent,
  type GridSelectRaw,
  type GridSelectResponse,
} from "./core";
import { GridSelectButtonsView, GridSelectRadioView } from "./views";

export const gridSelectModule: QuestionModule<
  GridSelectRaw,
  GridSelectDefinition,
  GridSelectDraft,
  GridSelectEvent,
  GridSelectResponse
> = {
  type: "grid_select",
  galleryFeatures: [
    "visual-groups",
    "row-custom-text",
    "baseline",
    "rows",
    "images",
    "row-images",
    "disabled",
    "custom-text",
    "restored",
  ],
  core: gridSelectCore,
  prompt: (definition) => ({
    content: definition.prompt,
    image: definition.image,
    altText: definition.altText,
    error: definition.error,
  }),
  views: {
    buttons: {
      id: "buttons",
      label: "Buttons",
      component: GridSelectButtonsView,
      space: "expanded",
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
    radio: {
      id: "radio",
      label: "Radio",
      component: GridSelectRadioView,
      space: "expanded",
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
  },
  defaultDisplay: { view: "buttons" },
  resolveDisplay: (raw) => ({
    view: raw.style === "button" ? "buttons" : raw.style,
  }),
  controls: (definition, draft, _display, copy) => ({
    specify: [
      ...rowSpecifyFields(
        definition.rows,
        draft.specifiedRows ?? {},
        (rowId, value): GridSelectEvent => ({
          type: "set-row-label",
          rowId,
          value,
        }),
      ),
      ...definition.rows.flatMap((row) =>
        definition.options.flatMap((option) =>
          option.customPrompt === undefined
            ? []
            : [
                {
                  target: {
                    kind: "option-answer" as const,
                    rowId: row.id,
                    optionId: option.id,
                  },
                  prompt: option.customPrompt,
                  value:
                    draft.selectedByRow[row.id]?.optionId === option.id
                      ? (draft.selectedByRow[row.id].customText ?? "")
                      : "",
                  selected: draft.selectedByRow[row.id]?.optionId === option.id,
                  disabled: option.disabled,
                  confirm: (value: string): GridSelectEvent => ({
                    type: "set-custom-answer",
                    rowId: row.id,
                    optionId: option.id,
                    value,
                  }),
                  remove: { type: "clear", rowId: row.id } as GridSelectEvent,
                },
              ],
        ),
      ),
    ],
    primaryAction: {
      purpose: "submit",
      label: copy.actions.send,
      action: { type: "submit" },
    },
  }),
};
